import type { CustomField } from "@repo/app-types/entities/customField";
import {
  customFieldDataSchemaByType,
  CustomFieldType,
} from "@repo/app-types/entities/customField";
import {
  checkMessageTemplateIncludesNextStepsVariable,
  MessageTemplateType,
} from "@repo/app-types/entities/messageTemplate";
import type { Project } from "@repo/app-types/entities/project";
import { SpecialProjectCustomFieldName } from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import {
  getIsTrainingProject,
  isRecruitingModeProject,
} from "@repo/app-types/helpers/utilities";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { type FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useAllMessageTemplatesQuery } from "../../../api/entities/messageTemplate.js";
import {
  useLazyProjectCustomFieldQuery,
  useUpdateProjectCustomFieldMutation,
} from "../../../api/entities/projectCustomField.js";
import { isFetchBaseQueryError } from "../../../api/helpers/errors.js";
import { appPaths } from "../../../app/appPaths.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { MessageTemplateField } from "../../messageTemplates/messageTemplateField.js";
import { updateProject } from "../projectsSlice.js";

export interface MessagingAutomationSectionProps {
  project: Project;
}

export const MessagingAutomationSection: FC<
  MessagingAutomationSectionProps
> = ({ project }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [acceptanceTemplateId, setAcceptanceTemplateId] = useState<
    string | null
  >(project.settings.acceptance_template_id);
  const [rejectionTemplateId, setRejectionTemplateId] = useState<string | null>(
    project.settings.rejection_template_id,
  );
  const [outreachTemplateId, setOutreachTemplateId] = useState<string | null>(
    project.settings.outreach_template_id,
  );

  const isAutomatedOutreachEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.AutomatedOutreach,
  );

  const [isSaving, setIsSaving] = useState(false);

  const { data: allMessageTemplates, isLoading } =
    useAllMessageTemplatesQuery();

  const acceptanceTemplates =
    allMessageTemplates?.filter(
      (template) => template.type === MessageTemplateType.Advanced,
    ) ?? [];
  const rejectionTemplates =
    allMessageTemplates?.filter(
      (template) => template.type === MessageTemplateType.Rejected,
    ) ?? [];
  const outreachTemplates =
    allMessageTemplates?.filter(
      (template) => template.type === MessageTemplateType.Outreach,
    ) ?? [];

  const advancedNextSteps = useAdvancedNextSteps(project.id);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        dispatch(
          updateProject({
            projectId: project.id,
            changes: {
              settings: {
                ...project.settings,
                acceptance_template_id: acceptanceTemplateId,
                rejection_template_id: rejectionTemplateId,
                outreach_template_id: outreachTemplateId,
              },
            },
          }),
        ).unwrap(),
        advancedNextSteps.save(),
      ]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setAcceptanceTemplateId(project.settings.acceptance_template_id);
    setRejectionTemplateId(project.settings.rejection_template_id);
    setOutreachTemplateId(project.settings.outreach_template_id);
    advancedNextSteps.reset();
  };

  const hasChanges =
    acceptanceTemplateId !== project.settings.acceptance_template_id ||
    rejectionTemplateId !== project.settings.rejection_template_id ||
    outreachTemplateId !== project.settings.outreach_template_id ||
    advancedNextSteps.hasChanges;

  const currentAcceptanceTemplate = acceptanceTemplates.find(
    (t) => t.id === acceptanceTemplateId,
  );
  const currentRejectionTemplate = rejectionTemplates.find(
    (t) => t.id === rejectionTemplateId,
  );

  const currentOutreachTemplate = outreachTemplates.find(
    (t) => t.id === outreachTemplateId,
  );

  const currentAcceptanceTemplateIncludesNextStepsVariable = useMemo(
    () =>
      currentAcceptanceTemplate !== undefined &&
      checkMessageTemplateIncludesNextStepsVariable(currentAcceptanceTemplate),
    [currentAcceptanceTemplate],
  );

  const isOutreachFieldVisible = isAutomatedOutreachEnabled;
  const isDecisionFieldsVisible = isRecruitingModeProject(project);

  const isAnyFieldVisible = isOutreachFieldVisible || isDecisionFieldsVisible;
  if (!isAnyFieldVisible) {
    return null;
  }

  const description = getIsTrainingProject(project)
    ? "Send automated messages to drive participant engagement in the training."
    : "Send automated candidate messages at key stages of the interview process.";

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <>
          <div>
            <Label size={Size.LARGE}>Messaging automation</Label>
            <div className="flex flex-col space-y-3">
              <div className="text-sm text-muted-foreground">
                {description} Manage your message templates in{" "}
                <span
                  className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                  onClick={() => {
                    navigate(appPaths.settingsMessageTemplates.build());
                  }}
                >
                  settings
                </span>
                .
              </div>
              {isOutreachFieldVisible ? (
                <MessageTemplateField
                  label="Outreach message"
                  template={currentOutreachTemplate}
                  templates={outreachTemplates}
                  onChange={(template) => {
                    setOutreachTemplateId(template?.id ?? null);
                  }}
                  isDisabled={!allowed}
                />
              ) : null}
              {isDecisionFieldsVisible ? (
                <>
                  <MessageTemplateField
                    label="Advancement message"
                    template={currentAcceptanceTemplate}
                    templates={acceptanceTemplates}
                    onChange={(template) => {
                      setAcceptanceTemplateId(template?.id ?? null);
                    }}
                    isDisabled={!allowed}
                  />
                  {currentAcceptanceTemplateIncludesNextStepsVariable ? (
                    <Input
                      label="Instructions for advanced candidates"
                      value={advancedNextSteps.value ?? ""}
                      onChange={(event) =>
                        advancedNextSteps.onChange(event.target.value)
                      }
                      placeholder="Please schedule your final round interview using this scheduling link: ____"
                      isDisabled={
                        !allowed || advancedNextSteps.value === undefined
                      }
                      maxLength={1000}
                      description="Next step instructions that will be inserted into the message template."
                    />
                  ) : null}
                  <MessageTemplateField
                    label="Rejection message"
                    template={currentRejectionTemplate}
                    templates={rejectionTemplates}
                    onChange={(template) => {
                      setRejectionTemplateId(template?.id ?? null);
                    }}
                    isDisabled={!allowed}
                  />
                </>
              ) : null}
              {hasChanges ? (
                <SaveAndCancelButtons
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isSaving={isSaving}
                  isSaveDisabled={!hasChanges}
                />
              ) : null}
            </div>
          </div>
        </>
      )}
    </Can>
  );
};

// TODO: This was way too hard. Refactor and simplify.
function useAdvancedNextSteps(projectId: string) {
  // The value shown in the UI. undefined until we've initialized it.
  const [value, setValue] = useState<string | undefined>(undefined);

  // The value from the server. undefined until we've fetched it.
  const [fetchedValue, setFetchedValue] = useState<string | undefined>(
    undefined,
  );

  // Use a lazy query, because we need to initialize our form field with a fresh value.
  const [query] = useLazyProjectCustomFieldQuery();
  const [mutate] = useUpdateProjectCustomFieldMutation();

  const onFetch = (customField: CustomField | null) => {
    // Parse the custom field data, defaulting to an empty value.
    const data = customField
      ? customFieldDataSchemaByType[CustomFieldType.Text].parse(
          customField.data,
        )
      : { value: null };
    const value = data.value ?? "";

    setFetchedValue(value);
    setValue(value);
  };

  useEffect(() => {
    // Reinitialize whenever the project ID changes.
    setFetchedValue(undefined);
    setValue(undefined);

    // Fetch the value from the server.
    let isCancelled = false;
    query({
      projectId,
      customFieldName: SpecialProjectCustomFieldName.AdvancedNextSteps,
    })
      .unwrap()
      .then((customField) => {
        if (isCancelled) {
          return;
        }

        onFetch(customField);
      })
      .catch((error) => {
        // This is expected when the custom field definition hasn't been provisioned yet.
        if (isFetchBaseQueryError(error) && error.status === 404) {
          return;
        }

        // TODO: Handle errors.
        console.error("Error fetching advanced next steps", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [projectId]);

  return {
    value,
    onChange: (nextValue: string) => {
      setValue((prevValue) => {
        if (prevValue === undefined) {
          throw new Error("useAdvancedNextSteps is not initialized");
        }

        return nextValue;
      });
    },
    hasChanges: value !== undefined && value !== fetchedValue,
    reset: () => {
      setValue(fetchedValue);
    },
    save: async () => {
      // Don't do anything if nothing was ever fetched.
      if (value === undefined) {
        return;
      }

      // Avoid doing any work in the common case where the value is empty and unchanged.
      if (!value.trim() && value === fetchedValue) {
        return;
      }

      try {
        const customField = await mutate({
          projectId,
          customFieldName: SpecialProjectCustomFieldName.AdvancedNextSteps,
          data: { value },
        }).unwrap();
        onFetch(customField);
      } catch (error) {
        // TODO: Handle errors.
        console.error("Error saving advanced next steps", error);
      }
    },
  };
}
